import { Box, Link, Step, StepLabel, Stepper, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { parseDateAndTime, sortByTimestamp } from '../../../helpers/UtilityHelper'
import { NAVY, SUBTLE, BORDER, ERROR, SUCCESS, WARNING, PRIMARY_BLUE, LIGHT_BG } from '../../../styles/foundation/colors'
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography'
import {
  DELIVERED_CODE, FAILED_DELIVERY_CODE, OFD_CODE,
  getRider, getScanLocation, lastIndexOfStatus, mapLinks,
} from './deliveryAttemptInfo'

const detailSx = {
  fontFamily: FONT,
  fontSize: '0.68rem',
  color: SUBTLE,
  mt: 0.25,
}

/** Map status codes to a semantic color */
function getStatusColor(code) {
  // Delivered
  if (code === 8) return SUCCESS
  // Cancelled, RTO, Failed
  if ([9, 10, 20].includes(code)) return ERROR
  // Out for delivery / pickup, in-transit
  if ([5, 6, 17, 18].includes(code)) return WARNING
  return PRIMARY_BLUE
}

const OrderTimeline = ({ orderDetailInfo, cancelledBy }) => {
  const orderHistory = useMemo(() => {
    const raw = orderDetailInfo?.order_history
      ? sortByTimestamp(orderDetailInfo.order_history, 'timestamp', 'creationDate')
      : []
    return [...raw].reverse()
  }, [orderDetailInfo?.order_history])

  const activeStep = useMemo(() => {
    for (let i = 0; i < orderHistory.length; i++) {
      if (orderHistory[i].orderStatusCode === 8) return orderHistory.length
    }
    return orderHistory.length - 1
  }, [orderHistory])

  // orderHistory is oldest-first, so the last index of a status is its latest scan. milestoneInfo only
  // describes the latest OFD / Delivered / Failed Delivery, so it is shown against those scans only.
  const latestIdx = useMemo(() => ({
    [OFD_CODE]: lastIndexOfStatus(orderHistory, OFD_CODE),
    [DELIVERED_CODE]: lastIndexOfStatus(orderHistory, DELIVERED_CODE),
    [FAILED_DELIVERY_CODE]: lastIndexOfStatus(orderHistory, FAILED_DELIVERY_CODE),
  }), [orderHistory])
  const rider = useMemo(() => getRider(orderDetailInfo), [orderDetailInfo])

  if (!orderHistory.length) {
    return (
      <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, fontStyle: 'italic' }}>
        No shipment scans available.
      </Typography>
    )
  }

  return (
    <Box
      sx={{
        overflowX: 'auto',
        bgcolor: LIGHT_BG,
        borderRadius: '10px',
        border: `1px solid ${BORDER}`,
        p: 2.5,
        /* custom scrollbar */
        '&::-webkit-scrollbar': { height: 6 },
        '&::-webkit-scrollbar-thumb': { bgcolor: BORDER, borderRadius: 3 },
      }}
    >
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          /* connector line */
          '& .MuiStepConnector-line': {
            borderColor: BORDER,
            borderTopWidth: 2,
          },
          /* completed connector */
          '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
            borderColor: SUCCESS,
          },
          /* active connector */
          '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
            borderColor: PRIMARY_BLUE,
          },
          /* step icon — completed */
          '& .MuiStepIcon-root.Mui-completed': {
            color: SUCCESS,
          },
          /* step icon — active */
          '& .MuiStepIcon-root.Mui-active': {
            color: PRIMARY_BLUE,
          },
          /* step icon text */
          '& .MuiStepIcon-text': {
            fontFamily: FONT,
            fontWeight: WEIGHT.semibold,
            fontSize: '0.65rem',
          },
        }}
      >
        {orderHistory.map((item, index) => {
          const statusColor = getStatusColor(item.orderStatusCode)
          return (
            <Step key={index} data-testid={`scan-${index}`} sx={{ minWidth: 180 }}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': { mt: 1 },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: SIZE['2xs'],
                    fontWeight: WEIGHT.semibold,
                    color: statusColor,
                    lineHeight: 1.3,
                  }}
                >
                  {item.orderStatusDescription}
                </Typography>

                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: '0.68rem',
                    fontWeight: WEIGHT.medium,
                    color: NAVY,
                    mt: 0.25,
                  }}
                >
                  {parseDateAndTime(item.timestamp)}
                </Typography>

                {item.currentLocation && (
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: '0.68rem',
                      color: SUBTLE,
                      mt: 0.25,
                    }}
                  >
                    {item.currentLocation}
                  </Typography>
                )}

                {item.remark && (
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: '0.68rem',
                      color: SUBTLE,
                      mt: 0.25,
                      fontStyle: 'italic',
                    }}
                  >
                    {item.remark}
                  </Typography>
                )}

                {item.orderStatusCode === OFD_CODE && index === latestIdx[OFD_CODE] && rider && (
                  <Typography sx={{ ...detailSx, color: NAVY }}>
                    Rider: {rider.name || '-'}{rider.phone ? ` · ${rider.phone}` : ''}
                  </Typography>
                )}

                {item.orderStatusCode === DELIVERED_CODE || item.orderStatusCode === FAILED_DELIVERY_CODE ? (
                  (() => {
                    const loc = getScanLocation(
                      item,
                      index === latestIdx[item.orderStatusCode],
                      orderDetailInfo?.milestoneInfo,
                    )
                    if (!loc) return null
                    return (
                      <>
                        <Typography sx={detailSx}>
                          Lat: {loc.lat}, Lng: {loc.lng}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', columnGap: 1, mt: 0.25 }}>
                          {mapLinks(loc.lat, loc.lng).map((l) => (
                            <Link
                              key={l.label}
                              href={l.href}
                              target='_blank'
                              rel='noopener noreferrer'
                              underline='hover'
                              sx={{ fontFamily: FONT, fontSize: '0.68rem', fontWeight: WEIGHT.medium, color: PRIMARY_BLUE }}
                            >
                              {l.label}
                            </Link>
                          ))}
                        </Box>
                      </>
                    )
                  })()
                ) : (
                  (item.additional?.latitude || item.additional?.longitude) && (
                    <Typography sx={detailSx}>
                      Lat: {item.additional.latitude || '-'}, Lng: {item.additional.longitude || '-'}
                    </Typography>
                  )
                )}

                {cancelledBy && item.orderStatusCode === 10 && (
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: '0.68rem',
                      fontWeight: WEIGHT.semibold,
                      color: ERROR,
                      mt: 0.25,
                    }}
                  >
                    Cancelled By: {cancelledBy}
                  </Typography>
                )}
              </StepLabel>
            </Step>
          )
        })}
      </Stepper>
    </Box>
  )
}

export default OrderTimeline
